import type { HotmartProductMapping } from "../services/chatbotV2/chatbotV2Service";

export const HOTMART_MEMBERS_IMPORT_URL = "https://app.hotmart.com/membership/list";

export function parseClubSubdomainFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const raw = url.trim();
  if (!raw) return null;
  const normalized = raw.includes("://") ? raw : `https://${raw.replace(/^\/+/, "")}`;
  try {
    const parsed = new URL(normalized);
    const host = parsed.hostname.toLowerCase();
    if (host.endsWith(".club.hotmart.com")) {
      const slug = host.slice(0, -".club.hotmart.com".length);
      return slug || null;
    }
    const parts = parsed.pathname.split("/").filter(Boolean);
    const clubIndex = parts.findIndex((part) => part.toLowerCase() === "club");
    if (clubIndex >= 0 && clubIndex + 1 < parts.length) {
      const candidate = parts[clubIndex + 1].trim().toLowerCase();
      if (/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(candidate)) {
        return candidate;
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function clubPublicUrlFromMapping(mapping: HotmartProductMapping): string {
  const value = mapping.metadata_json?.club_public_url;
  return typeof value === "string" ? value : "";
}

export function buildHotmartClubMappingPatch(
  mapping: HotmartProductMapping,
  patch: { club_public_url?: string; club_subdomain?: string }
): Partial<Pick<HotmartProductMapping, "club_subdomain" | "is_active">> & {
  metadata_json?: Record<string, unknown>;
  club_public_url?: string;
} {
  const metadata = { ...(mapping.metadata_json || {}) };
  const next: {
    club_subdomain?: string;
    metadata_json?: Record<string, unknown>;
    club_public_url?: string;
  } = {};

  if (patch.club_public_url !== undefined) {
    const trimmed = patch.club_public_url.trim();
    if (trimmed) {
      metadata.club_public_url = trimmed;
      next.club_public_url = trimmed;
    } else {
      delete metadata.club_public_url;
      next.club_public_url = "";
    }
  }

  if (patch.club_subdomain !== undefined) {
    next.club_subdomain = patch.club_subdomain.trim() || undefined;
  }

  next.metadata_json = metadata;
  return next;
}

export function hotmartAccessCaseStatusLabel(status: string): string {
  switch (status) {
    case "open":
      return "Abierto";
    case "in_progress":
      return "En progreso";
    case "resolved":
      return "Resuelto";
    case "dismissed":
      return "Descartado";
    default:
      return status;
  }
}

export function hotmartAccessCaseReasonLabel(reason: string | null | undefined): string {
  if (!reason) return "Motivo pendiente de registrar";
  const normalized = reason.trim().toLowerCase().replace(/\s+/g, "_");
  if (normalized === "purchase_found_no_club_user") {
    return "Compra encontrada, pero el alumno no aparece con acceso en el Club";
  }
  if (
    normalized.includes("purchase_approved_student_absent") ||
    normalized.includes("purchase_found_no_club_user")
  ) {
    return "Compra aprobada, pero el alumno no aparece con acceso en el Club";
  }
  if (normalized === "email_correction_request") {
    return "El comprador solicita corregir el correo asociado a la compra";
  }
  return reason.replace(/_/g, " ");
}

export function hotmartAccessCaseActionLabel(action: string | null | undefined): string {
  const normalized = action?.trim().toLowerCase().replace(/\s+/g, "_");
  switch (normalized) {
    case "queue_manual_activation":
      return "Importar o activar al alumno manualmente en Hotmart";
    case "create_access_case":
      return "Revisar la compra y activar o importar al alumno si corresponde";
    case "manual_review":
      return "Revisar el caso manualmente con la evidencia disponible";
    case "change_purchase_email":
      return "Revisar si el cambio de correo es elegible y hacerlo en Hotmart";
    case "confirm_product_membership":
      return "Confirmar en Hotmart que el alumno tiene asignado este producto";
    case "follow_delivery_instructions":
      return "Confirmar el acceso en la plataforma externa indicada";
    default:
      return action ? action.replace(/_/g, " ") : "Revisar el caso";
  }
}

export function hotmartAccessVerificationLabel(status: string | null | undefined): string {
  const normalized = status?.trim().toLowerCase().replace(/\s+/g, "_");
  switch (normalized) {
    case "not_checked":
      return "Sin revisar";
    case "purchase_approved_club_not_configured":
      return "Compra aprobada; falta configurar o confirmar el Club";
    case "verified_active":
      return "Acceso activo confirmado en Hotmart";
    case "verified_resolved":
      return "Acceso confirmado y caso resuelto";
    case "user_absent":
      return "El alumno todavía no aparece en el Club";
    case "active_in_club_product_unverified":
      return "Alumno activo en el Club; falta confirmar acceso a este producto";
    case "purchase_approved_student_absent":
      return "Compra aprobada; el alumno todavía no aparece en el Club";
    case "external_delivery":
      return "Entrega externa verificada; revisar acceso en la plataforma indicada";
    default:
      return normalized ? normalized.replace(/_/g, " ") : "Sin revisar";
  }
}

export function hotmartWebhookEventLabel(eventType: string): string {
  const labels: Record<string, string> = {
    CART_ABANDONMENT: "Carrito abandonado",
    CART_ABANDONED: "Carrito abandonado",
    PURCHASE_APPROVED: "Compra aprobada",
    PURCHASE_COMPLETE: "Compra completa",
    PURCHASE_COMPLETED: "Compra completa",
    PURCHASE_REFUNDED: "Compra reembolsada",
    PURCHASE_PROTEST: "Reembolso solicitado",
    PURCHASE_CHARGEBACK: "Chargeback",
    PURCHASE_DELAYED: "Compra atrasada",
    PURCHASE_CANCELED: "Compra cancelada",
    PURCHASE_EXPIRED: "Compra con plazo vencido",
    PURCHASE_BILLET_PRINTED: "Pago pendiente",
    PURCHASE_OUT_OF_SHOPPING_CART: "Compra fuera del checkout",
    ORDER_FULFILLMENT: "Datos logísticos actualizados",
    SUBSCRIPTION_CANCELLATION: "Suscripción cancelada",
    SUBSCRIPTION_REACTIVATED: "Suscripción reactivada",
    SUBSCRIPTION_BILLING_DATE_UPDATED: "Fecha de cobro actualizada",
    UPDATE_SUBSCRIPTION_CHARGE_DATE: "Fecha de cobro actualizada",
    SWITCH_PLAN: "Plan de suscripción cambiado",
    FIRST_ACCESS: "Primer acceso al Club",
    CLUB_FIRST_ACCESS: "Primer acceso al Club",
    CLUB_MODULE_COMPLETED: "Módulo completado",
  };
  return labels[eventType] || eventType.replace(/_/g, " ").toLowerCase();
}

export function hotmartProcessingStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    received: "Recibido",
    processing: "Procesando",
    processed: "Procesado",
    retry_scheduled: "Reintento programado",
    dead_letter: "Requiere intervención",
    failed: "Falló",
  };
  return labels[status] || status.replace(/_/g, " ");
}

export function hotmartCommercialStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    needs_consent: "Necesita consentimiento",
    eligible_for_owner_review: "Elegible para revisión",
    blocked_opt_out: "Bloqueado por opt-out",
    recovered: "Venta recuperada",
    dismissed: "Descartado",
  };
  return labels[status] || status.replace(/_/g, " ");
}

export function hotmartConsentLabel(status: string): string {
  const labels: Record<string, string> = {
    unknown: "Sin consentimiento comprobado",
    granted: "Consentimiento otorgado",
    opted_out: "Cliente pidió no recibir mensajes",
  };
  return labels[status] || status.replace(/_/g, " ");
}

export function hotmartEligibilityLabel(reason: string | null | undefined): string {
  const labels: Record<string, string> = {
    no_explicit_marketing_consent: "no se enviará nada sin consentimiento explícito",
    explicit_marketing_consent: "consentimiento explícito comprobado",
    contact_opted_out: "el cliente pidió no recibir mensajes",
  };
  return reason ? labels[reason] || reason.replace(/_/g, " ") : "sin evaluación";
}
